import {
  completePendingUserDraftPurge,
  recordPendingUserDraftPurge,
} from "@/features/composer-drafts/draftCleanupObligations";
import { flushComposerDrafts } from "@/features/composer-drafts/draftFlushRegistry";
import { removeDraftSessionShadowsForUser } from "@/features/composer-drafts/draftSessionShadow";
import {
  draftRepository,
  DraftStorageError,
} from "@/features/composer-drafts/draftRepository";

export type SafeAppNavigationReason =
  | "auth_reauthentication"
  | "auth_callback_return"
  | "chunk_load_recovery";

export interface SafeAppNavigationOptions {
  reason: SafeAppNavigationReason;
  navigate: () => void | Promise<void>;
  timeoutMs?: number;
}

const DEFAULT_NAVIGATION_FLUSH_TIMEOUT_MS = 1_500;
const LOGOUT_DRAFT_CLEANUP_TIMEOUT_MS = 500;

const waitForOperationOrTimeout = async (
  operation: Promise<void>,
  timeoutMs: number,
): Promise<void> => {
  let timeoutId!: number;
  const timeout = new Promise<void>((resolve) => {
    timeoutId = window.setTimeout(resolve, timeoutMs);
  });

  try {
    await Promise.race([operation, timeout]);
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const recordNavigationReason = (reason: SafeAppNavigationReason): void => {
  console.info(reason);
};

export const navigateAfterDraftFlush = async ({
  reason,
  navigate,
  timeoutMs = DEFAULT_NAVIGATION_FLUSH_TIMEOUT_MS,
}: SafeAppNavigationOptions): Promise<void> => {
  const flush = flushComposerDrafts().catch(() => undefined);
  await waitForOperationOrTimeout(flush, timeoutMs);

  recordNavigationReason(reason);
  await navigate();
};

export const removeUserDraftsBeforeLogout = async (
  userId: number,
): Promise<void> => {
  removeDraftSessionShadowsForUser(userId);
  // Record intent before touching IndexedDB so timeout/reload cannot forget it.
  recordPendingUserDraftPurge(userId);
  const cleanup = Promise.resolve()
    .then(() => draftRepository.removeForUser(userId))
    .then((result) => {
      if (!(result instanceof DraftStorageError)) {
        completePendingUserDraftPurge(userId);
      }
    })
    .catch(() => undefined);
  await waitForOperationOrTimeout(cleanup, LOGOUT_DRAFT_CLEANUP_TIMEOUT_MS);
};
